import React from 'react';
import { RotateCcw, UserX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SearchableSelect from '@/components/shared/SearchableSelect';
import PayrollMoneyInput from '@/components/payroll/PayrollMoneyInput';
import type { PayrollCalculatorEditableRow } from '@/lib/api/accounting';
import { formatRial } from '@/lib/formatMoney';
import {
  evaluatePayrollFormula,
  previewPayrollFormula,
} from '@/lib/payroll/payrollFormulas';
import {
  PAYROLL_NET_COLUMN_LETTER,
  PAYROLL_SPREADSHEET_COLUMNS,
  type PayrollSpreadsheetColumnId,
} from '@/lib/payroll/payrollSpreadsheetColumns';
import {
  netModeLabel,
  type PayrollRowPatch,
} from '@/lib/payrollCalculator';

type FormulaPickSession = {
  rowKey: string;
  text: string;
  cursor: number;
};

type PayrollSpreadsheetTableProps = {
  rows: PayrollCalculatorEditableRow[];
  onUpdateRow: (rowKey: string, patch: PayrollRowPatch) => void;
};

function netModeForRow(r: PayrollCalculatorEditableRow): PayrollRowPatch['net_mode'] {
  return r.net_mode === 'manual' ? 'manual' : r.net_mode === 'formula' ? 'formula' : 'auto';
}

const PayrollSpreadsheetTable: React.FC<PayrollSpreadsheetTableProps> = ({ rows, onUpdateRow }) => {
  const [formulaPick, setFormulaPick] = React.useState<FormulaPickSession | null>(null);
  const [formulaError, setFormulaError] = React.useState('');
  const formulaInputRef = React.useRef<HTMLInputElement | null>(null);

  const insertVariable = React.useCallback(
    (variable: string) => {
      setFormulaPick((prev) => {
        if (!prev) return prev;
        const before = prev.text.slice(0, prev.cursor);
        const after = prev.text.slice(prev.cursor);
        const spacer = before.length > 0 && !/[\s(+\-*/]$/.test(before) ? ' ' : '';
        const token = `${spacer}${variable}`;
        const nextText = before + token + after;
        const nextCursor = before.length + token.length;
        requestAnimationFrame(() => {
          const el = formulaInputRef.current;
          if (el) {
            el.focus();
            el.setSelectionRange(nextCursor, nextCursor);
          }
        });
        return { ...prev, text: nextText, cursor: nextCursor };
      });
    },
    [],
  );

  const startFormulaPick = (row: PayrollCalculatorEditableRow) => {
    setFormulaError('');
    const initial = row.net_formula?.trim() || '';
    const text = initial ? (initial.startsWith('=') ? initial : `=${initial}`) : '=';
    setFormulaPick({
      rowKey: row.row_key,
      text,
      cursor: text.length,
    });
    requestAnimationFrame(() => formulaInputRef.current?.focus());
  };

  const commitFormula = (row: PayrollCalculatorEditableRow) => {
    if (!formulaPick || formulaPick.rowKey !== row.row_key) return;
    const raw = formulaPick.text.trim().replace(/^=/, '').trim();
    if (!raw) {
      setFormulaPick(null);
      return;
    }
    try {
      const computed_net = evaluatePayrollFormula(raw, row);
      onUpdateRow(row.row_key, {
        net_mode: 'formula',
        net_formula: raw,
        computed_net,
      });
      setFormulaPick(null);
      setFormulaError('');
    } catch (e) {
      setFormulaError(e instanceof Error ? e.message : 'فرمول نامعتبر است');
    }
  };

  const cancelFormulaPick = () => setFormulaPick(null);

  const isPickingRow = (rowKey: string) => formulaPick?.rowKey === rowKey;

  return (
    <div className="space-y-2">
      {formulaPick ? (
        <div className="rounded-lg border border-violet-300 bg-violet-50 px-3 py-2 text-sm text-violet-950">
          <span className="font-medium">حالت انتخاب سلول (مثل اکسل):</span> روی ستون‌های همان ردیف کلیک کنید تا به
          فرمول اضافه شود. <span className="text-violet-700">Enter</span> = تأیید،{' '}
          <span className="text-violet-700">Esc</span> = انصراف
        </div>
      ) : (
        <p className="text-xs text-slate-500">
          برای فرمول سلولی: روی <strong>خالص پرداخت</strong> کلیک کنید یا <kbd className="rounded bg-slate-100 px-1">=</kbd>{' '}
          بزنید، سپس ستون‌ها را انتخاب کنید.
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1180px] border-collapse text-sm">
          <thead>
            <tr className="bg-slate-100 text-slate-700">
              <th className="border px-2 py-2 text-center w-12" title="خارج از محاسبه">
                —
              </th>
              <th className="border px-2 py-2 text-right">پرسنل</th>
              <th className="border px-2 py-2 text-right">نوع</th>
              {PAYROLL_SPREADSHEET_COLUMNS.map((col) => (
                <th key={col.id} className="border px-2 py-2 text-center">
                  <div className="font-mono text-[10px] text-slate-400">{col.letter}</div>
                  <div>{col.label}</div>
                </th>
              ))}
              <th className="border px-2 py-2 text-center">
                <div className="font-mono text-[10px] text-slate-400">{PAYROLL_NET_COLUMN_LETTER}</div>
                <div>خالص پرداخت</div>
              </th>
              <th className="border px-2 py-2 text-right">بانک</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const picking = isPickingRow(r.row_key);
              const bankOptions = r.bank_accounts.map((b) => ({
                value: String(b.id),
                label: b.label,
              }));

              const renderPickableCell = (colId: PayrollSpreadsheetColumnId, content: React.ReactNode) => {
                const col = PAYROLL_SPREADSHEET_COLUMNS.find((c) => c.id === colId)!;
                const canPick = picking && col.pickable;
                return (
                  <td
                    key={colId}
                    className={cn(
                      'border px-2 py-2 transition-colors',
                      canPick && 'cursor-cell bg-violet-50/80 ring-2 ring-inset ring-violet-300 hover:bg-violet-100',
                    )}
                    onClick={
                      canPick
                        ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            insertVariable(col.variable);
                          }
                        : undefined
                    }
                    title={canPick ? `افزودن ${col.variable} به فرمول` : undefined}
                  >
                    {content}
                  </td>
                );
              };

              const excluded = !!r.excluded;

              return (
                <tr
                  key={r.row_key}
                  className={cn(
                    'odd:bg-white even:bg-slate-50/60',
                    picking && 'bg-violet-50/30',
                    excluded && 'bg-slate-100/80 opacity-60',
                  )}
                >
                  <td className="border px-1 py-2 text-center">
                    {excluded ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-emerald-700 hover:bg-emerald-50"
                        title="بازگرداندن به محاسبه"
                        onClick={() => onUpdateRow(r.row_key, { excluded: false })}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-500 hover:bg-rose-50 hover:text-rose-700"
                        title="خارج از محاسبه (این ردیف در سند و اکسل نیست)"
                        onClick={() => onUpdateRow(r.row_key, { excluded: true })}
                      >
                        <UserX className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                  <td className="border px-2 py-2">
                    <div className={cn('font-medium', excluded && 'line-through text-slate-500')}>
                      {r.employee_name}
                    </div>
                    {r.employee_code ? (
                      <div className="font-mono text-xs text-slate-500" dir="ltr">
                        {r.employee_code}
                      </div>
                    ) : null}
                  </td>
                  <td className="border px-2 py-2 text-xs">{r.salary_type_label}</td>

                  {renderPickableCell(
                    'present_days',
                    <span className="block text-center font-mono">{r.present_days}</span>,
                  )}

                  {renderPickableCell(
                    'base_salary',
                    picking ? (
                      <span className="block text-center font-mono text-xs text-violet-900">
                        {formatRial(r.base_salary)}
                      </span>
                    ) : (
                      <PayrollMoneyInput
                        className="h-8 w-full min-w-[6rem]"
                        disabled={excluded}
                        value={r.base_salary}
                        onChange={(n) =>
                          onUpdateRow(r.row_key, { base_salary: n, net_mode: netModeForRow(r) })
                        }
                      />
                    ),
                  )}

                  {renderPickableCell(
                    'worked_salary',
                    picking ? (
                      <span className="block text-center font-mono text-xs text-violet-900">
                        {formatRial(r.worked_salary)}
                      </span>
                    ) : (
                      <PayrollMoneyInput
                        className="h-8 w-full min-w-[6rem]"
                        disabled={excluded}
                        value={r.worked_salary}
                        onChange={(n) =>
                          onUpdateRow(r.row_key, { worked_salary: n, net_mode: netModeForRow(r) })
                        }
                      />
                    ),
                  )}

                  {renderPickableCell(
                    'absence_deduction',
                    picking ? (
                      <span className="block text-center font-mono text-xs text-rose-800">
                        {formatRial(r.absence_deduction)}
                      </span>
                    ) : (
                      <PayrollMoneyInput
                        className="h-8 w-full min-w-[5rem] text-rose-700"
                        disabled={excluded}
                        value={r.absence_deduction}
                        onChange={(n) =>
                          onUpdateRow(r.row_key, { absence_deduction: n, net_mode: netModeForRow(r) })
                        }
                      />
                    ),
                  )}

                  {renderPickableCell(
                    'overtime_hours',
                    <span className="block text-center font-mono">{r.overtime_hours}</span>,
                  )}

                  {renderPickableCell(
                    'overtime_hourly_rate',
                    picking ? (
                      <span className="block text-center font-mono text-xs">{formatRial(r.overtime_hourly_rate)}</span>
                    ) : (
                      <PayrollMoneyInput
                        className="h-8 w-full min-w-[5rem]"
                        disabled={excluded}
                        value={r.overtime_hourly_rate}
                        onChange={(n) =>
                          onUpdateRow(r.row_key, { overtime_hourly_rate: n, net_mode: netModeForRow(r) })
                        }
                      />
                    ),
                  )}

                  {renderPickableCell(
                    'manual_advance_deduction',
                    picking ? (
                      <span className="block text-center font-mono text-xs">{formatRial(r.manual_advance_deduction)}</span>
                    ) : (
                      <PayrollMoneyInput
                        className="h-8 w-full min-w-[5rem]"
                        disabled={excluded}
                        value={r.manual_advance_deduction}
                        onChange={(n) =>
                          onUpdateRow(r.row_key, { manual_advance_deduction: n, net_mode: netModeForRow(r) })
                        }
                      />
                    ),
                  )}

                  {renderPickableCell(
                    'manual_incentive',
                    picking ? (
                      <span className="block text-center font-mono text-xs">{formatRial(r.manual_incentive)}</span>
                    ) : (
                      <PayrollMoneyInput
                        className="h-8 w-full min-w-[5rem]"
                        disabled={excluded}
                        value={r.manual_incentive}
                        onChange={(n) =>
                          onUpdateRow(r.row_key, { manual_incentive: n, net_mode: netModeForRow(r) })
                        }
                      />
                    ),
                  )}

                  <td
                    className={cn(
                      'border px-2 py-2',
                      picking && 'ring-2 ring-inset ring-violet-500 bg-white',
                    )}
                  >
                    <div className="flex min-w-[10rem] flex-col gap-1">
                      {picking ? (
                        <>
                          <div className="flex items-center gap-1">
                            <span className="font-mono text-violet-700">=</span>
                            <Input
                              ref={formulaInputRef}
                              className="h-8 flex-1 font-mono text-xs"
                              dir="ltr"
                              value={formulaPick?.text.replace(/^=/, '') ?? ''}
                              onChange={(e) => {
                                const body = e.target.value;
                                const cursor = e.target.selectionStart ?? body.length;
                                setFormulaPick((prev) =>
                                  prev
                                    ? { ...prev, text: `=${body}`, cursor: cursor + 1 }
                                    : prev,
                                );
                              }}
                              onSelect={(e) => {
                                const t = e.target as HTMLInputElement;
                                setFormulaPick((prev) =>
                                  prev
                                    ? { ...prev, cursor: (t.selectionStart ?? 0) + 1 }
                                    : prev,
                                );
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  commitFormula(r);
                                }
                                if (e.key === 'Escape') {
                                  e.preventDefault();
                                  cancelFormulaPick();
                                }
                              }}
                              placeholder="present_days * daily_salary"
                            />
                          </div>
                          {formulaPick?.text && formulaPick.text.length > 1 ? (
                            <p className="font-mono text-[10px] text-violet-800" dir="ltr">
                              پیش‌نمایش: {previewPayrollFormula(formulaPick.text.replace(/^=/, ''), r)}
                            </p>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <div onClick={(e) => e.stopPropagation()}>
                            <PayrollMoneyInput
                              className="h-8 font-semibold text-emerald-800"
                              disabled={excluded}
                              value={r.computed_net}
                              onChange={(n) =>
                                onUpdateRow(r.row_key, { computed_net: n, net_mode: 'manual' })
                              }
                            />
                          </div>
                          <div className="flex items-center justify-between gap-1">
                            <span
                              className={cn(
                                'rounded px-1 text-[10px]',
                                r.net_mode === 'manual' && 'bg-amber-100 text-amber-900',
                                r.net_mode === 'formula' && 'bg-violet-100 text-violet-900',
                                r.net_mode === 'auto' && 'bg-slate-100 text-slate-600',
                              )}
                            >
                              {netModeLabel(r.net_mode)}
                            </span>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-800 hover:bg-violet-200"
                                onClick={() => startFormulaPick(r)}
                                title="فرمول با انتخاب سلول"
                              >
                                ƒx
                              </button>
                              {r.net_mode !== 'auto' ? (
                                <button
                                  type="button"
                                  className="text-[10px] text-sky-700 hover:underline"
                                  onClick={() =>
                                    onUpdateRow(r.row_key, { net_mode: 'auto', net_formula: null })
                                  }
                                >
                                  خودکار
                                </button>
                              ) : null}
                            </div>
                          </div>
                          {formulaError && picking ? (
                            <p className="text-[10px] text-rose-600">{formulaError}</p>
                          ) : null}
                          {r.net_mode === 'formula' && r.net_formula && !picking ? (
                            <p
                              className="cursor-pointer font-mono text-[10px] text-violet-800 hover:underline"
                              dir="ltr"
                              title="کلیک برای ویرایش فرمول"
                              onClick={() => startFormulaPick(r)}
                            >
                              = {r.net_formula}
                            </p>
                          ) : null}
                          {!picking ? (
                            <button
                              type="button"
                              className="text-left text-[10px] text-violet-600 hover:underline"
                              onClick={() => startFormulaPick(r)}
                            >
                              + فرمول با کلیک روی ستون‌ها
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  </td>

                  <td className="border px-2 py-2 min-w-[10rem]">
                    {bankOptions.length > 1 ? (
                      <SearchableSelect
                        value={r.selected_bank_account_id ? String(r.selected_bank_account_id) : ''}
                        onChange={(v) =>
                          onUpdateRow(r.row_key, {
                            selected_bank_account_id: v ? Number(v) : null,
                          })
                        }
                        options={bankOptions}
                        placeholder="انتخاب بانک"
                        className="h-8 text-xs"
                      />
                    ) : (
                      <div className="text-xs text-slate-600">
                        {r.bank_name || '—'}
                        {r.iban ? (
                          <div dir="ltr" className="font-mono">
                            {r.iban}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PayrollSpreadsheetTable;
